import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-light-svg-icons';

export interface EvidenceImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  fileName?: string;
  'data-testid'?: string;
}

/**
 * Modal para mostrar evidencias (imágenes) en un popup
 * 
 * Permite visualizar imágenes de evidencias en un overlay modal
 * con soporte para cerrar con Escape y click fuera del modal
 */
export const EvidenceImageModal = ({
  isOpen,
  onClose,
  imageUrl,
  fileName,
  'data-testid': testId = 'evidence-image-modal',
}: EvidenceImageModalProps) => {
  const { t } = useTranslation('extraWorkOrdersModule');

  // Cerrar con tecla Escape y bloquear scroll
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const overlayContent = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid={testId}
    >
      {/* Backdrop con blur */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-lg" />

      {/* Contenedor de imagen flotante */}
      <div
        className="relative animate-in zoom-in-95 duration-300 ease-out max-w-[95vw] max-h-[95vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header con nombre de archivo y botón cerrar */}
        <div className="bg-body-bg border-card-border border p-4 rounded-t-2xl shadow-2xl">
          <div className="flex justify-between items-center gap-4">
            <div className="flex-1 min-w-0">
              {fileName && (
                <p className="text-sm font-medium text-title truncate" title={fileName}>
                  {fileName}
                </p>
              )}
            </div>

            {/* Botón de cerrar */}
            <button
              onClick={onClose}
              className="flex items-center justify-center w-8 h-8 rounded-full hover:bg-hover-press text-icons hover:text-text transition-all duration-200 hover:scale-105 flex-shrink-0"
              data-testid={`${testId}-close`}
              aria-label={t('detail.wastes.close_evidence', 'Cerrar evidencia')}
            >
              <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Imagen principal */}
        <div className="relative group flex-1 min-h-0">
          {/* Contenedor de imagen con mejor ajuste */}
          <div className="relative bg-body-bg rounded-b-2xl border border-card-border border-t-0 overflow-hidden h-full flex items-center justify-center">
            <img
              src={imageUrl}
              alt={fileName || t('detail.wastes.evidences', 'Evidencias')}
              className="block object-contain transition-all duration-300 group-hover:scale-[1.01] w-auto h-auto max-w-full max-w-[90vw] max-h-[calc(90vh-80px)] sm:max-w-[80vw] sm:max-h-[calc(85vh-80px)] md:max-w-[70vw] md:max-h-[calc(80vh-80px)] lg:max-w-[60vw] lg:max-h-[calc(75vh-80px)]"
              data-testid={`${testId}-image`}
              onError={(e) => {
                // Si la imagen falla al cargar, mostrar un mensaje
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const errorDiv = document.createElement('div');
                errorDiv.className = 'text-text text-center p-4';
                errorDiv.textContent = t('detail.wastes.image_load_error', 'Error al cargar la imagen');
                target.parentElement?.appendChild(errorDiv);
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );

  // Usar portal para renderizar en el body y evitar problemas de z-index
  return createPortal(overlayContent, document.body);
};
